import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Save, AlertCircle, Search, MapPinned, Loader, MapPin } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from "sonner";
import { loadGoogleMapsScript } from '../../utils/mapUtils';
import { API_BASE_URL, endpoints } from '../../config/api';
import { createPortal } from 'react-dom';
import { createClient } from "@supabase/supabase-js";
import { SUPABASE_CONFIG } from '../../supabaseClient';

// StyledAutocomplete component (copied and adapted from AddressEditPopup.js)
const StyledAutocomplete = ({ inputRef, onPlaceSelect, address, setAddress, isSaving, setGooglePlacesId }) => {
  const [predictions, setPredictions] = useState([]);
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!window.google?.maps?.places) return;
    if (!sessionToken.current && window.google.maps.places.AutocompleteSessionToken) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    if (!autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    return () => {
      setPredictions([]);
      // sessionToken.current = null; // Retain session token for the lifetime of the popup
    };
  }, []);

  // Calculate dropdown position when input is focused
  useEffect(() => {
    if (isFocused && inputRef.current) {
      const updatePosition = () => {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      };

      updatePosition();

      // Update position on window resize or scroll
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    }
  }, [isFocused, inputRef]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    setGooglePlacesId(''); // Clear place ID if address is manually changed

    if (!value || value.length < 2 || !autocompleteService.current) {
      setPredictions([]);
      return;
    }
    setIsLoadingPredictions(true);
    const request = {
      input: value,
      types: ['address'],
      sessionToken: sessionToken.current
    };
    autocompleteService.current.getPlacePredictions(
      request,
      (results, status) => {
        setIsLoadingPredictions(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
        } else {
          setPredictions([]);
        }
      }
    );
  };

  const handlePredictionClick = (placeId, description) => {
    if (isSaving) return;
    setAddress(description);
    setGooglePlacesId(placeId);
    setPredictions([]);
    setIsFocused(false);
    if (onPlaceSelect) {
      onPlaceSelect(placeId, description);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setPredictions([]);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputRef]);

  const limitedPredictions = predictions.slice(0, 4);

  return (
    <>
      <div className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          value={address}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          placeholder="Type to search for an address"
          className="w-full border border-gray-200 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm 
            focus:outline-none focus:ring-2 focus:ring-black/50 focus:border-transparent
            placeholder:text-gray-400"
          disabled={isSaving}
          autoComplete="off"
        />
      </div>

      {/* Portal the dropdown to avoid overflow issues */}
      {isFocused && createPortal(
        <AnimatePresence>
          {limitedPredictions.length > 0 && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: 8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: 8, height: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 999999 // Higher than the popup's z-index
              }}
            >
              {limitedPredictions.map((prediction) => (
                <motion.div
                  key={prediction.place_id}
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15 }}
                  whileHover={{ backgroundColor: '#f9fafb' }}
                  className="px-3 py-2.5 cursor-pointer flex items-start hover:bg-gray-50 border-b border-gray-50 last:border-0"
                  onClick={() => handlePredictionClick(prediction.place_id, prediction.description)}
                  data-dropdown-item="true"
                >
                  <MapPinned size={14} className="text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-800">{prediction.structured_formatting?.main_text || prediction.description}</span>
                    {prediction.structured_formatting?.secondary_text && (
                      <span className="text-xs text-gray-500 mt-0.5">{prediction.structured_formatting.secondary_text}</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
          {isLoadingPredictions && address.length >= 2 && limitedPredictions.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              className="absolute bg-white rounded-xl shadow-lg border border-gray-100 p-4 flex justify-center"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 999999
              }}
            >
              <Loader size={16} className="text-gray-400 animate-spin" />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

const AddLocationPopup = ({ 
  isOpen, 
  onClose, 
  supabase, 
  onSuccess, 
  businessType, 
  accountId 
}) => {
  const [googleAddress, setGoogleAddress] = useState('');
  const [googlePlacesId, setGooglePlacesId] = useState(''); // Keep this for backend logic but hide from UI
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mapsApiLoaded, setMapsApiLoaded] = useState(false);
  const addressInputRef = useRef(null);
  const popupRef = useRef(null);

  // When popup opens, focus on the input field and load maps
  useEffect(() => {
    if (isOpen) {
      setGoogleAddress('');
      setGooglePlacesId('');
      setError('');
      setIsLoading(false);

      // Focus on address input after a short delay
      const timer = setTimeout(() => {
        if (addressInputRef.current) {
          addressInputRef.current.focus();
        }
      }, 100);

      // Load Google Maps Script if not already loaded
      if (!mapsApiLoaded) {
        loadGoogleMapsScript()
          .then(() => {
            setMapsApiLoaded(true);
          })
          .catch(err => {
            console.error("Failed to load Google Maps for AddLocationPopup:", err);
            setError("Could not load address suggestions. Please enter manually.");
          });
      }

      return () => clearTimeout(timer);
    }
  }, [isOpen, mapsApiLoaded]);

  // Handle click outside to close popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the popup
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        // Also check if click is on a dropdown item (which are portaled to document.body)
        const isDropdownClick = event.target.closest('[data-dropdown-item]') || 
                               event.target.closest('.absolute.bg-white.rounded-xl.shadow-lg');

        if (!isDropdownClick) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const validateForm = () => {
    if (!googleAddress.trim()) {
      setError('Google address is required.');
      return false;
    }
    setError('');
    return true;
  };

  const handlePlaceSelected = (placeId, description) => {
    console.log("Place selected in AddLocationPopup:", placeId, description);
  };

  const handleSaveLocation = async () => {
    if (!validateForm()) {
      return;
    }

    if (!accountId) {
      setError('Account ID is not available. Cannot save.');
      toast.error('An unexpected error occurred. Please try again.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Use service role client for database operations
      const serviceRoleClient = createClient(
        SUPABASE_CONFIG.LAUNDROMAT.URL, 
        SUPABASE_CONFIG.LAUNDROMAT.SERVICE_KEY
      );

      // Check for duplicate address using service role client
      const { data: existingLocation, error: checkError } = await serviceRoleClient
        .from('vapi_location_settings')
        .select('name')
        .eq('account_id', accountId)
        .eq('google_address', googleAddress.trim())
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingLocation) {
        setError(`A location with this address already exists: "${existingLocation.name}"`);
        setIsLoading(false);
        return;
      }

      // Proceed with creating the new location using service role client
      const newLocationData = {
        account_id: accountId,
        name: googleAddress.split(',')[0].trim() || googleAddress.trim(),
        google_address: googleAddress.trim(),
        google_places_id: googlePlacesId.trim() || null, // Keep the backend logic
        business_type: businessType || 'Laundry Service',
        alternative_names: [],
        landmarks: [],
        roads: [],
        ivr_order: null,
        is_large_knowledge_base: false,
      };

      const { data: savedLocation, error: insertError } = await serviceRoleClient
        .from('vapi_location_settings')
        .insert([newLocationData])
        .select('id')
        .single();

      if (insertError) {
        throw insertError;
      }

      if (savedLocation && savedLocation.id) {
        try {
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
          if (sessionError || !sessionData.session) {
            console.error('Error getting session or no active session:', sessionError);
            toast.error('Location saved, but could not update context due to session issue.');
          } else {
            const accessToken = sessionData.session.access_token;
            const response = await fetch(`${API_BASE_URL}${endpoints.updateLocationContext}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
              },
              body: JSON.stringify({ location_id: savedLocation.id })
            });

            if (!response.ok) {
              const errorText = await response.text();
              console.error(`Error calling location context API:`, errorText);
              toast.error('Location saved, but failed to update extended context.');
            } else {
              console.log(`Location context updated successfully for location:`, savedLocation.id);
            }
          }
        } catch (contextError) {
          console.error('Failed to call context API after saving location:', contextError);
          toast.error('Location saved, but an error occurred while updating extended context.');
        }
      }

      toast.success('Location added successfully!');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving new location:', err);
      const errorMessage = err.message || 'Failed to save location. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      scale: 0.98, 
      opacity: 0,
      y: 10,
      transition: { duration: 0.2 }
    }
  };

  if (!isOpen) return null;

  // Portal the modal to ensure it's at the root level of the DOM
  const popupContent = (
    <AnimatePresence>
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={backdropVariants}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 overflow-hidden"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={onClose}
      >
        <motion.div
          ref={popupRef}
          variants={popupVariants}
          className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b sticky top-0 bg-white z-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                <MapPin size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
              </div>
              <div>
                <h2 className="font-medium text-base sm:text-lg text-gray-900">Add New Location</h2>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                  Add a new business location
                </p>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={16} className="sm:w-[18px] sm:h-[18px]" />
            </motion.button>
          </motion.div>

          {/* Content */}
          <div className="p-5 sm:p-7 space-y-4 sm:space-y-5 overflow-y-auto flex-1">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 sm:p-4 bg-red-50 border border-red-100 rounded-xl flex items-center text-red-600 text-xs sm:text-sm"
              >
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                {error}
              </motion.div>
            )}

            {/* Google Address Input */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="w-full"
            >
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Google Address <span className="text-red-500">*</span>
              </label>
              {mapsApiLoaded ? (
                <StyledAutocomplete
                  inputRef={addressInputRef}
                  address={googleAddress}
                  setAddress={setGoogleAddress}
                  onPlaceSelect={handlePlaceSelected}
                  isSaving={isLoading}
                  setGooglePlacesId={setGooglePlacesId}
                />
              ) : (
                <input
                  type="text"
                  value={googleAddress}
                  onChange={(e) => {
                    setGoogleAddress(e.target.value);
                    setGooglePlacesId(''); // Clear place ID if address is manually changed
                  }}
                  placeholder="Enter the full address"
                  className="w-full border border-gray-200 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm 
                    focus:outline-none focus:ring-2 focus:ring-black/50 focus:border-transparent
                    placeholder:text-gray-400"
                  disabled={isLoading || !mapsApiLoaded}
                />
              )}
              {!mapsApiLoaded && !error && (
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">Loading address suggestions...</p>
              )}
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                {googlePlacesId ? "Address verified with Google Maps" : "Type to search or enter manually"}
              </p>
            </motion.div>
          </div>

          {/* Footer */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="px-5 sm:px-7 py-4 bg-gray-50 flex justify-end gap-2 sm:gap-3 sticky bottom-0 border-t"
          >
            <motion.button
              whileHover={{ scale: 1.02, backgroundColor: '#f9fafb' }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl border border-gray-200 text-gray-700 text-xs sm:text-sm font-medium
                bg-white hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSaveLocation}
              disabled={isLoading}
              className="py-2 sm:py-2.5 px-4 sm:px-5 rounded-xl bg-black text-white text-xs sm:text-sm font-medium
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="mr-2 h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  Saving...
                </div>
              ) : (
                "Add Location"
              )}
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  // Use a portal to render the modal at the document level
  return createPortal(popupContent, document.body);
};

export default AddLocationPopup; 